import isNil from 'lodash/fp/isNil';

import { type IAsset, IAssetType, type IAssetValue, type IDerivativeDetails } from '../../../generated/graphql';
import { logWarnOnce } from '../../log.utils.ts';
import type { RecursivePartial } from '../../type.utils.ts';

/**
 * @deprecated Use `IAsset` instead
 */
export type PublicAsset = Pick<IAsset, 'symbol' | 'id' | 'icon' | 'name'> & {
  type: IAssetType.Public;
  label: string;
};

/**
 * @deprecated Use `IAsset` instead
 */
export type ExchangeAsset = Pick<IAsset, 'name' | 'symbol' | 'id'> & {
  type: IAssetType.Exchange;
  exchangeDetails: Label;
};

/**
 * @deprecated Use `IAsset` instead
 */
export type DerivativeAsset = Pick<IAsset, 'symbol' | 'id'> & {
  type: IAssetType.Derivative;
  derivativeDetails: IDerivativeDetails;
};

/**
 * @deprecated Use `IAsset` instead
 */
export type PrivateAsset = Pick<IAsset, 'symbol' | 'id'> & {
  type: IAssetType.Private;
  name: string;
};

/**
 * @deprecated Use `IAsset` instead
 */
export interface PrivateAssetWithPrivateDetails extends PrivateAsset {
  privateDetails: {
    unvestedAsset?: {
      id: string;
      symbol: string;
    };
  };
}

/**
 * @deprecated Use `IAsset` instead
 */
export type Asset = PublicAsset | ExchangeAsset | DerivativeAsset | PrivateAsset;

/**
 * @deprecated Use `IAssetValue` instead
 */
export type AssetValue = Omit<IAssetValue, 'asset'> & { asset: Asset };

/**
 * @deprecated Use `IAsset` instead
 */
export type IAssetToAsset<T> = T extends IAsset ? Asset : NonNullable<T> extends IAsset ? Asset | undefined : T;

/**
 * @deprecated Use `IAsset` instead
 */
export type IAssetValueToAssetValue<T> = {
  [key in keyof T]: NonNullable<T[key]> extends IAsset ? IAssetToAsset<T[key]> : T[key];
};

const logAssetIfDoesntMatchTypeGuard = (asset: RecursivePartial<IAsset>, matches: boolean): boolean => {
  if (!matches) {
    logWarnOnce('Asset doesnt have all fields: ' + JSON.stringify(asset));

    return false;
  }

  return true;
};

/**
 * @deprecated explicitly validate that type has required fields
 */
export const isPublicAsset = (
  asset: Omit<IAsset, 'derivativeDetails' | 'unvestedAsset' | 'priceAsset' | 'underlyingAsset'>
): asset is PublicAsset => {
  // don't log warning for checking only asset type. Log only once we miss some fields which we expect to be set
  if (asset.type !== IAssetType.Public) {
    return false;
  }

  return logAssetIfDoesntMatchTypeGuard(
    asset,
    !isNil(asset.label) && !isNil(asset.symbol) && !isNil(asset.name) && !isNil(asset.id)
  );
};

/**
 * @deprecated explicitly validate that type has required fields
 */
export const getPublicAssets = <T extends { label?: Label | null; name?: string | null; type: IAssetType }>(
  assets: T[]
): (Omit<T, 'label' | 'name' | 'derivativeDetails' | 'unvestedAsset' | 'type' | 'icon'> & {
  label: Label;
  name: string;
  type: IAssetType.Public;
})[] =>
  assets
    .filter((asset: T): asset is T & { label: Label; name: string } => {
      return asset.type === IAssetType.Public && !isNil(asset.label) && !isNil(asset.name);
    })
    .map((val) => ({
      ...val,
      type: IAssetType.Public,
    }));

/**
 * @deprecated explicitly validate that type has required fields
 */
export const getPrivateAndPublicAssets = <
  T extends { id: string; symbol: string; label?: Label | null; name?: string | null; type: IAssetType },
>(
  assets: T[]
): Array<PublicAsset | PrivateAsset> => {
  return assets.filter((asset: T) => isPublicAsset(asset) || isPrivateAsset(asset));
};

/**
 * @deprecated explicitly validate that type has required fields
 */
export const getAssets = (assets: Omit<IAsset, 'priceAsset' | 'underlyingAsset'>[]): Asset[] => {
  return assets.filter((asset: Omit<IAsset, 'priceAsset' | 'underlyingAsset'>): asset is Asset => isAsset(asset));
};

/**
 * @deprecated explicitly validate that type has required fields
 */
export const isAsset = (asset: Omit<IAsset, 'priceAsset' | 'underlyingAsset'> | null | undefined): asset is Asset => {
  if (isNil(asset)) {
    return false;
  }

  switch (asset.type) {
    case IAssetType.Derivative:
      return isDerivativeAsset(asset);
    case IAssetType.Exchange:
      return isExchangeAsset(asset);
    case IAssetType.Private:
      return isPrivateAsset(asset);
    case IAssetType.Public:
      return isPublicAsset(asset);
    default:
      console.error('IAsset doesnt have all asset fields', asset);
      return false;
  }
};

/**
 * @deprecated explicitly validate that type has required fields
 */
export const isDerivativeAsset = (asset: Omit<IAsset, 'priceAsset' | 'underlyingAsset'>): asset is DerivativeAsset => {
  if (asset.type !== IAssetType.Derivative) {
    return false;
  }

  return logAssetIfDoesntMatchTypeGuard(
    asset,
    !isNil(asset.derivativeDetails) && !isNil(asset.symbol) && !isNil(asset.id)
  );
};

/**
 * @deprecated explicitly validate that type has required fields
 */
export const isPrivateAssetWithPrivateDetails = (
  asset: RecursivePartial<IAsset>
): asset is PrivateAssetWithPrivateDetails => {
  if (asset.type !== IAssetType.Private) {
    return false;
  }

  // private details is optional, when asset doesnt have unvested asset, it may not be present
  return isPrivateAsset(asset);
};

/**
 * @deprecated explicitly validate that type has required fields
 */
export const isPrivateAsset = (asset: RecursivePartial<IAsset>): asset is PrivateAsset => {
  if (asset.type !== IAssetType.Private) {
    return false;
  }

  return logAssetIfDoesntMatchTypeGuard(asset, !isNil(asset.name) && !isNil(asset.symbol) && !isNil(asset.id));
};

/**
 * @deprecated explicitly validate that type has required fields
 */
export const isExchangeAsset = (
  asset: Omit<IAsset, 'unvestedAsset' | 'priceAsset' | 'underlyingAsset'>
): asset is ExchangeAsset => {
  if (asset.type !== IAssetType.Exchange) {
    return false;
  }

  return logAssetIfDoesntMatchTypeGuard(
    asset,
    !isNil(asset.symbol) && !isNil(asset.id) && !isNil(asset.exchangeDetails)
  );
};

/**
 * @deprecated explicitly validate that type has required fields
 */
export const isNonDerivativeAsset = (
  asset: Omit<IAsset, 'priceAsset' | 'underlyingAsset'>
): asset is Exclude<Asset, DerivativeAsset> => {
  return asset.type !== IAssetType.Derivative;
};

/**
 * @deprecated explicitly validate that type has required fields
 */
export const filterByAssetType = <T extends { id: string; symbol: string; type: IAssetType }>(
  idToAsset: Record<string, IAsset>,
  predicate: (val: Omit<IAsset, 'priceAsset' | 'underlyingAsset'>) => val is T
): Record<string, T> => {
  const assets = Object.values(idToAsset)
    .map((value): [string, T | undefined] => [value.id, predicate(value) ? value : undefined])
    .filter((isToValue): isToValue is [string, T] => !isNil(isToValue[1]));

  return Object.fromEntries(assets);
};

export const DOLLAR_LABEL = 'spt:us-dollar/usd';
